import { ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

export type ContentCardProps = {
  children?: ReactNode;
  className?: string;
  /** An anchor for a page that links down to its own sections, as `Container` takes one. */
  id?: string;
};

/**
 * A white panel to put a section of a page's content on — a border, a radius and the
 * padding that goes with them.
 *
 * Structure only, deliberately: it says nothing about type, so the page inside it styles
 * its own copy (`prose`, wherever the card holds running text) and this stays usable by a
 * page that is not running text at all — /contact puts a form in one. The reading
 * treatment belongs to the copy, not to the panel around it: `prose` restyles every
 * element beneath it, so a card carrying a form or an alert must not be given one.
 *
 * The one thing it does say is that its first and last children keep their margins to
 * themselves, because the padding here would otherwise be doubled at each end — a rule
 * that belongs to whatever supplies the padding.
 *
 * The class list is merged rather than concatenated, for the reason `ButtonLink` spells
 * out: a caller's `className` should replace the default it collides with, and with
 * `clsx` both land on the element and the compiled sheet decides. That silently ignored
 * the caller — /guarantee's table card asks for `p-0` and got `p-5`, because Tailwind
 * emits the padding scale in order and `p-5` comes last.
 */
export default function ContentCard({ children, className, id }: ContentCardProps) {
  return (
    <section
      id={id}
      className={twMerge(
        'rounded-lg border border-solid border-slate-200 bg-white p-5 shadow-sm md:p-8',
        '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
        className
      )}
    >
      {children}
    </section>
  );
}
